import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getContractorDetails, clearContractorsError, clearContractorsSuccess } from '../../../../redux/Slices/contractorsSlice';
import { FaArrowLeft, FaEdit, FaBuilding, FaPhone, FaEnvelope, FaMapMarkerAlt, FaIdCard, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

// contractor details component
const ContractorDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { details: contractorDetails, isLoading, error, success } = useSelector((state) => state.contractors);
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const lastErrorRef = useRef({ message: null, time: 0 });

  useEffect(() => {
    if (id) {
      dispatch(getContractorDetails(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearContractorsSuccess());
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearContractorsError()), 3000);
    }
  }, [success, error, dispatch, isRTL]);

  const handleEdit = () => {
    navigate(`/contractors/edit/${id}`);
  };

  const handleBack = () => {
    navigate('/contractors');
  };

  const getContractorName = () => {
    if (!contractorDetails) return '';
    return isRTL ? (contractorDetails.name_ar || contractorDetails.name_en) : (contractorDetails.name_en || contractorDetails.name_ar);
  };
 

  if (isLoading) {
    return (
      <div style={{
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        direction: dir,
        textAlign
      }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('contractors.loading')}</span>
        </div>
      </div>
    );
  }

  if (!contractorDetails) {
    return (
      <div style={{
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        direction: dir,
        textAlign
      }}>
        <div style={{ textAlign: 'center' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#ffc107', marginBottom: '20px' }}></i>
          <h5 style={{ color: 'white', marginBottom: '10px' }}>{t('contractors.details.notFound')}</h5>
          <p style={{ color: '#6c757d', marginBottom: '20px' }}>{t('contractors.details.notFoundDescription')}</p>
          <button
            onClick={handleBack}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              padding: '12px 25px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              margin: '0 auto',
              flexDirection: isRTL ? 'row' : 'row-reverse'
            }}
          >
            <FaArrowLeft />
            {t('contractors.details.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir,
      textAlign
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: '#1b3350',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #333',
          marginBottom: '20px'
        }}>
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #333',
            backgroundColor: '#1a1f2e',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h1 style={{
              margin : 0,
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'white',
              textAlign
            }}>
              {t('contractors.details.title')}
            </h1>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleBack}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse'
                }}
              >
                <FaArrowLeft />
                {t('contractors.details.back')}
              </button>
              <button
                onClick={handleEdit}
                style={{
                  backgroundColor: '#AC2000',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse'
                }}
              >
                <FaEdit />
                {t('contractors.details.edit')}
              </button>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: '#1b3350',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #333'
        }}>
          <div style={{ padding: '30px' }}>
            <div style={{ marginBottom: '30px' }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '20px',
                textAlign,
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <FaBuilding />
                {t('contractors.details.name')}
              </h2>
              <div style={{
                backgroundColor: '#1a1f2e',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid #333'
              }}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '5px',
                    color: '#999',
                    fontSize: '12px',
                    textAlign
                  }}>
                    {t('contractors.details.nameAr')}
                  </label>
                  <p style={{ color: 'white', fontSize: '16px', margin: 0, textAlign }}>
                    {contractorDetails.name_ar || '-'}
                  </p>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '5px',
                    color: '#999',
                    fontSize: '12px',
                    textAlign
                  }}>
                    {t('contractors.details.nameEn')}
                  </label>
                  <p style={{ color: 'white', fontSize: '16px', margin: 0, textAlign }}>
                    {contractorDetails.name_en || '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div style={{ marginBottom: '30px' }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '20px',
                textAlign
              }}>
                {t('contractors.details.contactInfo')}
              </h2>
              <div style={{
                backgroundColor: '#1a1f2e',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid #333',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px'
              }}>
                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px',
                    color: '#999',
                    fontSize: '12px',
                    textAlign
                  }}>
                    <FaIdCard />
                    {t('contractors.details.taxNumber')}
                  </label>
                  <p style={{ color: 'white', fontSize: '14px', margin: 0, textAlign }}>
                    {contractorDetails.tax_number || '-'}
                  </p>
                </div>
                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px',
                    color: '#999',
                    fontSize: '12px',
                    textAlign
                  }}>
                    <FaPhone />
                    {t('contractors.details.phone')}
                  </label>
                  <p style={{ color: 'white', fontSize: '14px', margin: 0, textAlign }}>
                    {contractorDetails.phone || '-'}
                  </p>
                </div>
                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px',
                    color: '#999',
                    fontSize: '12px',
                    textAlign
                  }}>
                    <FaEnvelope />
                    {t('contractors.details.email')}
                  </label>
                  <p style={{ color: 'white', fontSize: '14px', margin: 0, textAlign }}>
                    {contractorDetails.email || '-'}
                  </p>
                </div>
                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px',
                    color: '#999',
                    fontSize: '12px',
                    textAlign
                  }}>
                    <FaMapMarkerAlt />
                    {t('contractors.details.address')}
                  </label>
                  <p style={{ color: 'white', fontSize: '14px', margin: 0, textAlign }}>
                    {contractorDetails.address || '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Status */}
            <div>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '20px',
                textAlign
              }}>
                {t('contractors.details.status')}
              </h2>
              <div style={{
                backgroundColor: '#1a1f2e',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid #333',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span style={{
                  color: contractorDetails.is_active ? '#28a745' : '#dc3545',
                  fontSize: '24px'
                }}>
                  {contractorDetails.is_active ? <FaToggleOn /> : <FaToggleOff />}
                </span>
                <span style={{
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}>
                  {contractorDetails.is_active ? t('contractors.details.active') : t('contractors.details.inactive')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractorDetails;

